import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { GripVertical, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";

interface KanbanViewProps {
  table: any;
}

export const KanbanView = ({ table }: KanbanViewProps) => {
  const { user } = useAuth();
  const [fields, setFields] = useState<any[]>([]);
  const [allFields, setAllFields] = useState<any[]>([]);
  const [records, setRecords] = useState<any[]>([]);
  const [groupField, setGroupField] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (table) {
      fetchFields();
      fetchAllFields();
      fetchRecords();
    }
  }, [table]);

  const fetchFields = async () => {
    try {
      const { data, error } = await supabase
        .from("fields")
        .select("*")
        .eq("table_id", table.id)
        .in("type", ["single_select", "multi_select"])
        .order("order_index", { ascending: true });

      if (error) throw error;
      if (data && data.length > 0) {
        setGroupField(data[0]);
      }
      setFields(data || []);
    } catch (error: any) {
      toast.error("Failed to load fields");
    }
  };

  const fetchAllFields = async () => {
    try {
      const { data, error } = await supabase
        .from("fields")
        .select("*")
        .eq("table_id", table.id)
        .order("order_index", { ascending: true });

      if (error) throw error;
      setAllFields(data || []);
    } catch (error: any) {
      console.error("Failed to load all fields");
    }
  };

  const fetchRecords = async () => {
    try {
      const { data, error } = await supabase
        .from("records")
        .select("*")
        .eq("table_id", table.id);

      if (error) throw error;
      setRecords(data || []);
    } catch (error: any) {
      toast.error("Failed to load records");
    } finally {
      setLoading(false);
    }
  };

  const createRecordInStatus = async (status: string) => {
    if (!user || !groupField) return;
    
    try {
      const { data, error } = await supabase
        .from("records")
        .insert([{
          table_id: table.id,
          data: { [groupField.name]: status },
          created_by: user.id
        }])
        .select()
        .single();

      if (error) throw error;
      setRecords([...records, data]);
      toast.success("Record created");
    } catch (error: any) {
      toast.error("Failed to create record");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!groupField) {
    return (
      <div className="flex items-center justify-center h-full p-8">
        <div className="text-center max-w-md">
          <div className="h-16 w-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
            <GripVertical className="h-8 w-8 text-primary" />
          </div>
          <h3 className="text-lg font-semibold mb-2">No status field found</h3>
          <p className="text-muted-foreground text-sm">
            Add a "Single Select" or "Multi Select" field to use Kanban view. This field will be used to organize your cards into columns.
          </p>
        </div>
      </div>
    );
  }

  const statuses = groupField.config?.options || ["To Do", "In Progress", "Done"];
  
  const getRecordsByStatus = (status: string) => {
    return records.filter(r => r.data?.[groupField.name] === status);
  };

  const getDisplayField = () => {
    return allFields.find(f => f.name !== groupField.name) || allFields[0];
  };

  const statusColors: Record<string, string> = {
    "To Do": "bg-slate-500/10 text-slate-600 dark:text-slate-400",
    "In Progress": "bg-amber-500/10 text-amber-600 dark:text-amber-400",
    "Done": "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
  };

  return (
    <div className="h-full overflow-x-auto p-4 md:p-6">
      <div className="flex gap-4 md:gap-6 min-w-max">
        {statuses.map((status: string) => {
          const statusRecords = getRecordsByStatus(status);
          const displayField = getDisplayField();
          
          return (
            <div key={status} className="flex-shrink-0 w-72 md:w-80">
              <div className="mb-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className={statusColors[status] || "bg-muted"}>
                    {status}
                  </Badge>
                  <span className="text-xs text-muted-foreground font-medium">
                    {statusRecords.length}
                  </span>
                </div>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-7 w-7"
                  onClick={() => createRecordInStatus(status)}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              <div className="space-y-3">
                {statusRecords.map((record) => (
                  <Card 
                    key={record.id} 
                    className="cursor-pointer hover:shadow-lg hover:border-primary/30 transition-all duration-200 border-border/50"
                  >
                    <CardHeader className="p-4 pb-2">
                      <CardTitle className="text-sm font-medium line-clamp-2">
                        {record.data?.[displayField?.name] || "Untitled"}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-4 pt-0">
                      <div className="space-y-1.5">
                        {allFields
                          .filter(f => f.name !== groupField.name && f.name !== displayField?.name)
                          .slice(0, 2)
                          .map((field) => {
                            const value = record.data?.[field.name];
                            if (!value) return null;
                            return (
                              <div key={field.id} className="text-xs text-muted-foreground truncate">
                                <span className="font-medium">{field.name}:</span> {String(value)}
                              </div>
                            );
                          })}
                      </div>
                    </CardContent>
                  </Card>
                ))}
                {statusRecords.length === 0 && (
                  <div className="border-2 border-dashed border-border/50 rounded-lg p-6 text-center">
                    <p className="text-xs text-muted-foreground">No items</p>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
